// © Microsoft Corporation. All rights reserved.

import { CommonExperienceProps, TelemetryTags } from "@msnews/experiences-base";

import { AppError } from "@msnews/diagnostics";
import { ContentActionsToolbarClassNames } from ".";
import { ManagedClasses } from "@microsoft/fast-components-class-name-contracts-msft";
import { SharingToolbarProps } from "./components/sharing-toolbar";
import { Status } from "./ContentActionsToolbar.constants";

/**
 * Toolbar positioning options
 */
export enum ToolbarPosition {
    horizontal = "horizontal",
    vertical = "vertical"
}

/**
 * An interface to represent the ContentActionsToolbar jss managed classes.
 * @export
 * @interface ContentActionsToolbarManagedClasses
 */
export type ContentActionsToolbarManagedClasses = ManagedClasses<ContentActionsToolbarClassNames>;

/**
 * An interface to represent the ContentActionsToolbar handled props.
 * @export
 * @interface ContentActionsToolbarHandledProps
 */
export interface ContentActionsToolbarHandledProps extends CommonExperienceProps, ContentActionsToolbarManagedClasses {
    /**
     * Defines the toolbar position in case of single static position. Undefined otherwise.
     */
    staticPosition?: ToolbarPosition;

    /**
     * Position to render toolbar in
     * @type {ToolbarPosition}
     */
    position?: ToolbarPosition;

    /**
     * Sharing toolbar props
     * @type {SharingToolbarProps}
     */
    sharingToolbar?: SharingToolbarProps;

    /**
     * Content Status
     * @type {Status}
     */
    contentStatus?: Status;

    /**
     * Sharing toolbar error
     * @type {Error}
     */
    sharingError?: Error;

    /**
     * Root element telemetry tags
     * @type {TelemetryTags}
     */
    rootTelemetryTags?: TelemetryTags;
}

/**
 * An interface to represent the ContentActionsToolbar unhandled props.
 * @export
 * @interface ContentActionsToolbarUnhandledProps
 */
export type ContentActionsToolbarUnhandledProps = React.HTMLAttributes<HTMLElement>;

/**
 * The ContentActionsToolbar props type
 */
export type ContentActionsToolbarProps = ContentActionsToolbarHandledProps & ContentActionsToolbarUnhandledProps;

/**
 * App Error Event Tracking Callback Signature
 */
export type TrackAppErrorEventSignature = (error: AppError) => void;

/**
 * Click event tracking callback signature
 */
export type TrackTelemetryEventSignature = (event: Object, element: HTMLElement, destination: string, headline: string) => void;